<script setup lang="ts">
import type { EventVault } from "@/models"
import type { IMenuItem } from "@/types"
import { computed } from "vue"
import { useTranslation } from "@/composables"
import { EventVaultStatus } from "@/models"
import { AppLink, Button, ButtonLink, Icon } from "../ui"

const props = defineProps<{
  eventVault: EventVault
}>()

const emit = defineEmits<{
  (e: "showLinks"): void
  (e: "showExport", eventVaultId?: number): void
  (e: "showExtend", eventVaultId?: number): void
  (e: "showDelete", eventVaultId?: number): void
  (e: "showOnboardingGuide"): void
}>()

const { t } = useTranslation()

const isSettingsDisabled = computed(
  () => props.eventVault.status === EventVaultStatus.Expired,
)

const isSubscriptionEvent = computed(() =>
  Boolean(props.eventVault?.packageSubscriptionId),
)

const isFreemiumEvent = computed(() => Boolean(props.eventVault?.freemiumMode))

const settingsPreviewItems = computed(() => {
  return {
    [t("summary_page.settings_card.settings.guests_can_view_event")]:
      props.eventVault?.eventVaultSettings?.sharePixWithGuests,
    [t("summary_page.settings_card.settings.guest_uploads_enabled")]:
      !props.eventVault?.eventVaultSettings?.disableGuestUpload,
    [t("summary_page.settings_card.settings.guest_downloads_enabled")]:
      props.eventVault?.eventVaultSettings?.allowGuestDownload,
  }
})

const customiseEventLink = computed(() => {
  const { eventVaultSettings, id } = props.eventVault

  if (eventVaultSettings?.allowEventTemplate) {
    return `/event/${id}/template/apply`
  }

  if (eventVaultSettings?.albumsEnabled) {
    return `/event/${id}/customize`
  }

  return null
})

const quickLinks = computed(() => {
  const eventVaultId = props.eventVault?.id

  const links: Record<string, IMenuItem> = {
    view_links: {
      key: "view_links",
      label: t("summary_page.links_card.links.view_links"),
      onClick: () => emit("showLinks"),
    },
    view_gallery: {
      key: "view_gallery",
      label: t("summary_page.links_card.links.view_gallery"),
      to: `/guest/${eventVaultId}/gallery`,
    },
    view_guestbook: {
      key: "view_guestbook",
      label: t("summary_page.links_card.links.view_guestbook"),
      to: `/guest/${eventVaultId}/messages`,
    },
    manage_co_hosts: {
      key: "manage_co_hosts",
      label: t("summary_page.links_card.links.manage_co_hosts"),
      to: `/event/${eventVaultId}/users`,
    },
    download_zip: {
      key: "download_zip",
      label: t("summary_page.links_card.links.download_zip"),
      onClick: () => emit("showExport", eventVaultId),
    },
    upgrade: {
      key: "upgrade",
      label: t("summary_page.links_card.links.upgrade"),
    },
    extend: { key: "extend", label: t("summary_page.links_card.links.extend") },
    delete: { key: "delete", label: t("summary_page.links_card.links.delete") },
    manage_albums: {
      key: "manage_albums",
      label: t("summary_page.links_card.links.manage_albums"),
    },
    customise_theme: {
      key: "customise_theme",
      label: t("summary_page.links_card.links.customise_theme"),
    },
    manage_subscription: {
      key: "manage_subscription",
      label: t("summary_page.links_card.links.manage_subscription"),
    },
    setup_guide: {
      key: "setup_guide",
      label: t("summary_page.links_card.links.setup_guide"),
      onClick: () => emit("showOnboardingGuide"),
    },
  }

  if (props.eventVault.eventVaultSettings?.albumsEnabled)
    links.manage_albums.to = `/event/${eventVaultId}/albums`

  if (customiseEventLink.value)
    links.customise_theme.to = customiseEventLink.value

  if (isSubscriptionEvent.value) {
    links.manage_subscription.href
      = "https://guestpix.com/my-account/subscriptions/"
  }

  if (!isSubscriptionEvent.value) {
    links.extend.onClick = () => emit("showExtend", eventVaultId)
    links.delete.onClick = () => emit("showDelete", eventVaultId)
    if (props.eventVault?.freemiumMode && props.eventVault?.upgradeUrl) {
      links.upgrade.href = props.eventVault.upgradeUrl
      links.upgrade.to = undefined
    }
    else {
      links.upgrade.to = `/event/${eventVaultId}/upgrade`
      links.upgrade.href = undefined
    }
  }

  if (customiseEventLink.value) {
    links.customise_theme.to = customiseEventLink.value
  }

  return links
})

const statusLinks: Record<EventVaultStatus, IMenuItem[]> = {
  [EventVaultStatus.Draft]: [
    quickLinks.value.view_links,
    quickLinks.value.view_gallery,
    quickLinks.value.manage_albums,
    quickLinks.value.upgrade,
    ...(isFreemiumEvent.value ? [quickLinks.value.setup_guide] : []),
  ],
  [EventVaultStatus.Ready]: [
    quickLinks.value.view_links,
    quickLinks.value.view_gallery,
    quickLinks.value.manage_albums,
    quickLinks.value.upgrade,
    ...(isFreemiumEvent.value ? [quickLinks.value.setup_guide] : []),
  ],
  [EventVaultStatus.Live]: [
    quickLinks.value.view_links,
    quickLinks.value.view_gallery,
    quickLinks.value.manage_albums,
    quickLinks.value.customise_theme,
    quickLinks.value.upgrade,
    quickLinks.value.download_zip,
    ...(isFreemiumEvent.value ? [quickLinks.value.setup_guide] : []),
  ],
  [EventVaultStatus.Closed]: [
    quickLinks.value.view_links,
    quickLinks.value.view_gallery,
    quickLinks.value.manage_albums,
    quickLinks.value.customise_theme,
    quickLinks.value.download_zip,
  ],
  [EventVaultStatus.Expiring]: [
    ...(isFreemiumEvent.value
      ? [
          quickLinks.value.view_links,
          quickLinks.value.view_gallery,
          quickLinks.value.manage_albums,
          quickLinks.value.customise_theme,
          quickLinks.value.upgrade,
        ]
      : [
          quickLinks.value.view_links,
          quickLinks.value.view_gallery,
          quickLinks.value.manage_albums,
          quickLinks.value.customise_theme,
          quickLinks.value.extend,
          quickLinks.value.manage_subscription,
        ]),
  ],
  [EventVaultStatus.Expired]: [
    ...(isFreemiumEvent.value
      ? [
          quickLinks.value.view_gallery,
          quickLinks.value.delete,
          quickLinks.value.upgrade,
        ]
      : [
          quickLinks.value.view_gallery,
          quickLinks.value.extend,
          quickLinks.value.delete,
          quickLinks.value.manage_subscription,
        ]),
  ],
  [EventVaultStatus.ExtendInProgress]: [],
  [EventVaultStatus.DeletingInProgress]: [],
  [EventVaultStatus.Archived]: [],
  [EventVaultStatus.All]: [],
}

const visibleStatusLinks = computed(() => {
  const links = statusLinks[props.eventVault.status] ?? []
  return links.filter(link => Boolean(link.to || link.onClick || link.href))
})
</script>

<template>
  <div class="flex flex-col border border-secondary rounded-xl overflow-hidden">
    <h2 class="font-comfortaa text-lg font-bold p-4 bg-tertiary">
      {{ t("summary_page.settings_card.title") }}
    </h2>
    <div class="flex flex-col gap-4 p-4 justify-between h-full">
      <div class="flex flex-col gap-4 md:gap-8">
        <div class="flex flex-col gap-2">
          <div
            v-for="(value, key) in settingsPreviewItems"
            :key="key"
            class="flex items-center gap-2 justify-between"
          >
            <p>{{ key }}</p>
            <Icon
              :icon="value ? 'Check' : 'Close'"
              :class="value ? 'text-green-500' : 'text-red-500'"
            />
          </div>
        </div>

        <div class="flex flex-col">
          <h3 class="font-semibold mb-2">
            {{ t("summary_page.links_card.title") }}
          </h3>
          <ul class="flex flex-col gap-2">
            <li
              v-for="link in visibleStatusLinks"
              :key="link.key"
              class="block cursor-pointer text-left text-link underline hover:text-link-hover focus:text-link-hover"
            >
              <AppLink v-if="link.to" :to="link.to">
                {{ link.label }}
              </AppLink>
              <button
                v-else-if="link.onClick"
                class="underline cursor-pointer"
                @click.prevent="link.onClick"
              >
                {{ link.label }}
              </button>
              <a v-else-if="link.href" :href="link.href" target="_blank">
                {{ link.label }}
              </a>
            </li>
          </ul>
        </div>
      </div>

      <Button
        v-if="isSettingsDisabled"
        disabled
        variant="tertiary"
        prefix-icon="SettingsCog"
      >
        {{ t("summary_page.settings_card.manage_settings_btn") }}
      </Button>
      <ButtonLink
        v-else
        :to="`/event/${eventVault.id}/advanced-settings`"
        variant="tertiary"
        prefix-icon="SettingsCog"
      >
        {{ t("summary_page.settings_card.manage_settings_btn") }}
      </ButtonLink>
    </div>
  </div>
</template>
